import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import echo from '../lib/echo';

interface PricePayload {
    symbol: string;
    instrument_id: string;
    bid: number;
    ask: number;
    last: number;
    provider?: string;
    timestamp?: number;
}

/**
 * Subscribes to the public `prices` WebSocket channel via Reverb.
 * Keeps React Query caches for all-prices-instruments, crm-prices, and
 * per-instrument current-instrument-price up-to-date without polling.
 *
 * Call once near the top of the authenticated layout.
 */
export function usePriceSocket() {
    const queryClient = useQueryClient();

    useEffect(() => {
        const channel = echo.channel('prices');

        channel.listen('.price.updated', (payload: PricePayload) => {
            const {
                instrument_id,
                symbol,
                bid,
                ask,
                last,
                provider = 'backend',
                timestamp = Date.now(),
            } = payload;

            const entry = { symbol, bid, ask, last, provider, timestamp };

            // Update all-prices-instruments map
            queryClient.setQueryData<Map<string, typeof entry>>(
                ['all-prices-instruments'],
                (old = new Map()) => {
                    const next = new Map(old);
                    next.set(instrument_id, entry);
                    return next;
                }
            );

            // Update crm-prices map
            queryClient.setQueryData<Map<string, typeof entry>>(
                ['crm-prices'],
                (old = new Map()) => {
                    const next = new Map(old);
                    next.set(instrument_id, entry);
                    return next;
                }
            );

            // Update open per-instrument price queries
            queryClient
                .getQueriesData<typeof entry | null>({ queryKey: ['current-instrument-price'] })
                .forEach(([queryKey, current]) => {
                    if (current?.symbol === symbol) {
                        queryClient.setQueryData(queryKey, entry);
                    }
                });
        });

        return () => {
            echo.leaveChannel('prices');
        };
    }, [queryClient]);
}
